import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Network, Server, Cloud, Shield, Router, Headphones, CheckCircle, Calendar } from 'lucide-react';

const ServicesPage = () => {
  const [showModal, setShowModal] = useState(false);

  const mainServices = [
    {
      id: 'network-infrastructure',
      icon: Network,
      title: 'Network Infrastructure',
      description: 'Build a reliable, scalable network foundation that supports your business growth.',
      features: [
        { id: 'net-1', text: 'Network design and architecture planning' },
        { id: 'net-2', text: 'LAN/WAN installation and configuration' },
        { id: 'net-3', text: 'Network performance optimization and monitoring' },
        { id: 'net-4', text: 'Structured cabling and infrastructure setup' }
      ],
      benefits: [
        { id: 'net-b1', title: 'High Performance', desc: 'Fast, reliable connectivity for all devices and users' },
        { id: 'net-b2', title: 'Enhanced Security', desc: 'Built-in security measures to protect your data' },
        { id: 'net-b3', title: 'Scalable Solutions', desc: 'Infrastructure that grows with your business' }
      ]
    },
    {
      id: 'router-switch',
      icon: Router,
      title: 'Router & Switch Configuration',
      description: 'Expert configuration and management of routers and switches to ensure optimal network performance.',
      features: [
        { id: 'rtr-1', text: 'Enterprise-grade router setup and configuration' },
        { id: 'rtr-2', text: 'Managed and unmanaged switch deployment' },
        { id: 'rtr-3', text: 'VLAN configuration and network segmentation' },
        { id: 'rtr-4', text: 'Quality of Service (QoS) optimization' }
      ]
    },
    {
      id: 'office-365',
      icon: Cloud,
      title: 'Office 365 Setup & Migration',
      description: 'Seamless transition to Microsoft 365 with complete setup, data migration, and ongoing support.',
      features: [
        { id: 'o365-1', text: 'Microsoft 365 account setup and licensing' },
        { id: 'o365-2', text: 'Email migration from legacy systems' },
        { id: 'o365-3', text: 'SharePoint and Teams deployment' },
        { id: 'o365-4', text: 'User training and adoption support' }
      ]
    },
    {
      id: 'server-management',
      icon: Server,
      title: 'Server Management',
      description: 'Complete server administration and maintenance',
      features: [
        { id: 'srv-1', text: 'Windows & Linux servers' },
        { id: 'srv-2', text: 'Virtual machine setup' },
        { id: 'srv-3', text: 'Backup and recovery' },
        { id: 'srv-4', text: 'Performance monitoring' }
      ]
    },
    {
      id: 'cybersecurity',
      icon: Shield,
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions',
      features: [
        { id: 'sec-1', text: 'Firewall configuration' },
        { id: 'sec-2', text: 'Security audits' },
        { id: 'sec-3', text: 'Antivirus deployment' },
        { id: 'sec-4', text: 'Threat protection' }
      ]
    },
    {
      id: 'it-support',
      icon: Headphones,
      title: 'IT Support',
      description: 'Dedicated technical assistance',
      features: [
        { id: 'sup-1', text: '24/7 help desk' },
        { id: 'sup-2', text: 'Remote support' },
        { id: 'sup-3', text: 'On-site assistance' },
        { id: 'sup-4', text: 'Ticket management' }
      ]
    }
  ];

  const equipment = [
    { id: 'eq-cisco', name: 'Cisco enterprise routers and switches' },
    { id: 'eq-juniper', name: 'Juniper Networks equipment' },
    { id: 'eq-hp', name: 'HP/Aruba networking hardware' },
    { id: 'eq-ubiquiti', name: 'Ubiquiti UniFi systems' },
    { id: 'eq-dell', name: 'Dell networking solutions' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-cyan-500 uppercase tracking-wide mb-2">Professional IT Services</p>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Comprehensive IT Services</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            From network infrastructure to cloud solutions, we provide end-to-end IT services that keep your business running smoothly and securely.
          </p>
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="services-hero-consult">
            <Calendar className="w-5 h-5 mr-2" />
            Book a Free Consult
          </Button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mainServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="border-2 border-gray-200 overflow-hidden">
                  <CardHeader className="bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{service.title}</CardTitle>
                        <CardDescription className="text-base mt-1">{service.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Features:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature) => (
                            <li key={feature.id} className="flex items-start">
                              <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{feature.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {service.benefits && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                          <div className="space-y-3">
                            {service.benefits.map((benefit) => (
                              <div key={benefit.id}>
                                <h5 className="font-semibold text-gray-900">{benefit.title}</h5>
                                <p className="text-sm text-gray-600">{benefit.desc}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Equipment We Work With</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipment.map((item) => (
              <div key={item.id} className="flex items-center space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Need a Custom IT Solution?</h2>
          <p className="text-xl text-blue-50 mb-8">
            Every business is unique. Let's discuss your specific IT requirements and create a tailored solution.
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl px-8" onClick={() => setShowModal(true)} data-testid="services-cta-consult">
            <Calendar className="w-5 h-5 mr-2" />
            Book a Free Consult
          </Button>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default ServicesPage;
